/**
 * PWA Service — Service Worker registration, offline dossier caching, and install prompt handling.
 * Enables Aura Traveler to work as a fully installable Progressive Web App with offline dossier viewing.
 */

// Register service worker
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | undefined> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
            }
          });
        }
      });

      return registration;
    } catch (error) {
      console.error('[PWA] Registration failed:', error);
    }
  }
  return undefined;
};

// Cache a dossier for offline viewing
export const cacheDossierOffline = (tripId: string, tripData: any): void => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_DOSSIER',
      tripId,
      data: tripData,
    });
  }
};

// Retrieve a cached dossier (for offline viewing)
export const getCachedDossier = async (tripId: string): Promise<any | null> => {
  if ('caches' in window) {
    try {
      const cache = await caches.open('aura-dossiers-v1');
      const response = await cache.match(new Request(`/dossier/${tripId}`));
      if (response) {
        return await response.json();
      }
    } catch (error) {
      console.error('[PWA] Failed to retrieve cached dossier:', error);
    }
  }
  return null;
};

// Check if running as installed PWA
export const isInstalledPWA = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

// --- Install Prompt Management ---
let deferredPrompt: any = null;
let installPromptListeners: Array<(canInstall: boolean) => void> = [];

export const initInstallPrompt = (): void => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    // Notify all listeners that install is available
    installPromptListeners.forEach(fn => fn(true));
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installPromptListeners.forEach(fn => fn(false));
  });
};

export const canInstall = (): boolean => deferredPrompt !== null;

export const onInstallAvailableChange = (callback: (canInstall: boolean) => void): (() => void) => {
  installPromptListeners.push(callback);
  return () => {
    installPromptListeners = installPromptListeners.filter(fn => fn !== callback);
  };
};

export const promptInstall = async (): Promise<boolean> => {
  if (!deferredPrompt) return false;
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  installPromptListeners.forEach(fn => fn(false));
  return outcome === 'accepted';
};

// Check if the app is online
export const isOnline = (): boolean => navigator.onLine;

// Listen for online/offline events
export const onConnectivityChange = (callback: (online: boolean) => void): (() => void) => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};
